// Vendor
import { FC, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// Components
import Gap from 'src/components/atoms/Gap';
import { Icon } from 'src/components/atoms/Icon';
import { Row } from 'src/components/atoms/Row';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { Filter, SelectedFilterData } from 'src/components/organisms/Filter';
import InputSearch from 'src/components/organisms/InputSearch';
import { SelectedSortData, Sort } from 'src/components/organisms/Sort';
import { useFdeInfo, useSetFdeInbox } from 'src/features/fdeInbox';
import { PropertyMultiSelector } from '../PropertyMultiSelector';
import { Container, StyledToolTip } from './styles';
import useGetSelectedData from './useGetSelectedData';

// Types
import { CheckboxData } from 'src/components/atoms/Checkbox/types';

// Hooks
import usePropertiesDDown from 'src/features/property/hooks/usePropertiesDDown';
import { getDefaultValues } from './constants';

export type IFormValues = {
  properties?: CheckboxData[];
};

const FDEInboxToolbar: FC = () => {
  const { filters, isToolbarDisabled } = useFdeInfo();
  const { filtering, sorting, searching, clearSearch, clearAllFilters } = useSetFdeInbox();
  const { selectedFilterData, selectedSortData, buildFilterQuery } = useGetSelectedData();
  const justClearedRef = useRef(false);

  const { onResetGetPropertiesDDown } = usePropertiesDDown();

  const methods = useForm<IFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      properties: undefined
    }
  });

  useEffect(() => {
    return () => {
      onResetGetPropertiesDDown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultValues = useMemo(() => getDefaultValues(), []);

  const filterPanelData = defaultValues.filterPanel;

  const handleSearch = (value?: string) => {
    searching(value || undefined);
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handleFilter = (selected: SelectedFilterData) => {
    const query = buildFilterQuery(selected) || undefined;

    filtering(query);
  };

  const handleClearAllFilters = () => {
    justClearedRef.current = true;
    clearAllFilters();
    methods.reset();
    setTimeout(() => {
      justClearedRef.current = false;
    }, 100);
  };

  const handleSort = (selected: SelectedSortData) => {
    const query = selected?.field ? `${selected?.field}:${selected?.order}` : undefined;

    sorting(query);
  };

  return (
    <Container>
      <Row alignItems="center" gap={1.5}>
        <Row.Col size={3}>
          <InputSearch
            name="search"
            value={filters?.q || ''}
            placeholder="Search by applicant name"
            onSearch={({ value }) => handleSearch(value as string)}
            onClear={handleClearSearch}
            isDisabled={isToolbarDisabled}
          />
        </Row.Col>
        <Row.Col size={2}>
          <FormProvider {...methods}>
            <PropertyMultiSelector
              methods={methods}
              isDisabled={isToolbarDisabled}
              onCallback={({ data: properties }) => {
                methods.setValue('properties', properties);
              }}
              onApply={() => {
                if (justClearedRef.current) {
                  return;
                }

                const properties = methods
                  .getValues('properties')
                  ?.map((property) => property.value)
                  .join('|');

                const selected = { ...selectedFilterData, properties: properties || undefined };

                handleFilter(selected);
              }}
            />
          </FormProvider>
        </Row.Col>
        <Row.Col size={1} alignContent="left">
          <StyledToolTip
            direction={ToolTipDirection.bottom_fit}
            content="Use the properties filter to narrow your search to specific properties. 'Select All' to search across all properties you have access to."
          >
            <Icon icon="help" />
          </StyledToolTip>
        </Row.Col>
      </Row>
      <Gap height={1} />
      <Row alignItems="center" gap={1.5}>
        <Filter
          filterText="Filter will be applied in all applicant folders to the newest applicant entry."
          data={filterPanelData}
          selected={selectedFilterData}
          onSelect={(selected) => {
            handleFilter({ properties: selectedFilterData.properties, ...selected });
          }}
          onClearAll={handleClearAllFilters}
          isDisabled={isToolbarDisabled}
        />
        <Sort
          data={defaultValues.sortPanel}
          selected={selectedSortData}
          onSelect={handleSort}
          isDisabled={isToolbarDisabled}
        />
        <StyledToolTip
          direction={ToolTipDirection.bottom_fit}
          content="Sorting and filtering will be applied in all folders to the last application received."
        >
          <Icon icon="help" />
        </StyledToolTip>
      </Row>
    </Container>
  );
};

export default FDEInboxToolbar;
